import { CaseReducerActions, Dispatch, PayloadAction, SliceCaseReducers } from "@reduxjs/toolkit";
import moment from "moment";

export const validateBusinessRegistrationNumber = (businessNumber: string) => {
    // 1. 입력 정리: 하이픈 제거 및 숫자만 추출
    const cleanedNumber = businessNumber.replace(/-/g, '');

    // 2. 자리수 확인: 총 10자리인지 확인
    if (!/^\d{10}$/.test(cleanedNumber)) {
        return false;
    }

    // 3. 각 자리수에 가중치 곱하기
    const weights = [1, 3, 7, 1, 3, 7, 1, 3, 5];
    let sum = 0;

    for (let i = 0; i < weights.length; i++) {
        sum += parseInt(cleanedNumber.charAt(i), 10) * weights[i];
    }

    // 4. 특정 자리수 추가 계산: 9번째 자리(인덱스 8) 숫자에 5를 곱하고 10으로 나눈 몫을 합계에 더하기
    const secondLastDigit = parseInt(cleanedNumber.charAt(8), 10);
    sum += Math.floor((secondLastDigit * 5) / 10);

    // 5. 최종 검증: 10에서 (합계 % 10)을 뺀 값이 마지막 자리 숫자와 일치하는지 확인
    const checkDigit = parseInt(cleanedNumber.charAt(9), 10);
    const calculatedCheckDigit = (10 - (sum % 10)) % 10;

    return checkDigit === calculatedCheckDigit;
};

export const validateResidentNumber = (residentNumber: string): boolean => {
    // 1. 입력값에서 숫자 이외의 문자 제거
    const cleanNumber = residentNumber.replace(/\D/g, '');

    // 2. 길이 검사 (13자리)
    if (cleanNumber.length !== 13) {
        return false; // 길이가 맞지 않음
    }

    // 3. 가중치 배열 정의
    const multipliers = [2, 3, 4, 5, 6, 7, 8, 9, 2, 3, 4, 5];
    let sum = 0;

    // 4. 각 자리의 숫자에 가중치를 곱하고 합산
    for (let i = 0; i < multipliers.length; i++) {
        const digit = parseInt(cleanNumber.charAt(i), 10);
        if (isNaN(digit)) {
            return false; // 숫자가 아닌 문자가 포함되어 있음
        }
        sum += digit * multipliers[i];
    }

    // 5. 합산 결과를 이용하여 검증 숫자 계산
    const remainder = sum % 11;
    let checkDigit = 11 - remainder;
    if (checkDigit >= 10) {
        checkDigit = checkDigit % 10;
    }

    // 6. 주민등록번호의 마지막 자리와 비교
    const lastDigit = parseInt(cleanNumber.charAt(12), 10);
    if (isNaN(lastDigit)) {
        return false; // 마지막 문자가 숫자가 아님
    }

    return checkDigit === lastDigit;
}

export const xadomInputs = ["fake-input", "xadom-select", "fake-date", "toggle-container", "XadomBtn", "XadomArea"];

export const getAllXadomInputs = (styles?: { [key: string]: string }) => {
    const classes = xadomInputs.map(selector =>
        styles ? `.${styles[selector.replace('.', '')]}` : `[class*="${selector}"]`
    );
    const allElements = Array.from(document.querySelectorAll(classes.join(', ')));
    return allElements;
};

export const getAllXadomInputsInRow = (currentInput: HTMLElement, styles?: { [key: string]: string }) => {
    const currentRow = currentInput.closest('tr');
    if (!(currentRow instanceof HTMLElement)) return [];

    const classes = xadomInputs.map(selector =>
        styles ? `.${styles[selector.replace('.', '')]}` : selector
    );
    return Array.from(currentRow.querySelectorAll(classes.join(', ')));
}

export const getAllXadomInputsInTable = (currentInput: HTMLElement, styles?: { [key: string]: string }): HTMLElement[] => {
    const currentTable = currentInput.closest('table');
    if (!currentTable) return [];

    const classes = xadomInputs.map(selector =>
        styles ? `.${styles[selector.replace('.', '')]}` : selector
    );
    return Array.from(currentTable.querySelectorAll(classes.join(', ')));
};

interface PositionGrid {
    rows: HTMLElement[][];
    positions: Map<HTMLElement, { row: number; col: number }>;
}

// 위치 기반 그리드 생성 함수
const buildPositionGrid = (elements: HTMLElement[]): PositionGrid => {
    const sorted = [...elements].sort((a, b) => {
        const rectA = a.getBoundingClientRect();
        const rectB = b.getBoundingClientRect();
        return rectA.top - rectB.top || rectA.left - rectB.left;
    });

    const grid: PositionGrid = { rows: [], positions: new Map() };
    let currentRow: HTMLElement[] = [];
    let lastTop = -Infinity;

    sorted.forEach(element => {
        const { top } = element.getBoundingClientRect();
        if (Math.abs(top - lastTop) > 10) { // 새로운 행 감지 (10px 임계값)
            if (currentRow.length) grid.rows.push(currentRow);
            currentRow = [];
            lastTop = top;
        }
        currentRow.push(element);
    });

    if (currentRow.length) grid.rows.push(currentRow);

    // 위치 정보 맵 생성
    grid.rows.forEach((row, rowIndex) => {
        row.forEach((element, colIndex) => {
            grid.positions.set(element, { row: rowIndex, col: colIndex });
        });
    });

    return grid;
};

// 요소 위치 찾기 함수
const findElementPosition = (grid: PositionGrid, element: HTMLElement) => {
    return grid.positions.get(element) || { row: -1, col: -1 };
};

/**
 * Focus the next input field.
 * @param inputRef - Reference to the current input element.
 * @param lockArea - If true, restrict focus within the current table.
 */
export const focusNextInput = (
    inputRef: React.RefObject<HTMLElement>,
    lockArea: boolean = false
): boolean => {
    const currentInput = inputRef.current;
    if (!currentInput) return false;

    const allInputFields = lockArea
        ? getAllXadomInputsInTable(currentInput)
        : getAllXadomInputs();

    const totalFields = allInputFields.length;
    if (totalFields === 0) return false;

    let currentIndex = allInputFields.indexOf(currentInput);
    if (currentIndex === -1) return false;

    if (currentIndex === totalFields - 1) return false;

    let nextIndex = currentIndex + 1;
    let fieldsChecked = 0;

    while (fieldsChecked < totalFields - currentIndex) {
        const nextInputField = allInputFields[nextIndex] as HTMLElement;

        if (!nextInputField.classList.contains('readonly')) {
            nextInputField.focus();
            return true;
        }

        nextIndex++;
        fieldsChecked++;

        if (nextIndex >= totalFields) break;
    }

    return false; // 포커스 가능한 요소를 찾지 못한 경우
};

/**
 * Focus the previous input field.
 * @param inputRef - Reference to the current input element.
 * @param lockArea - If true, restrict focus within the current table.
 */
export const focusPreviousInput = (
    inputRef: React.RefObject<HTMLElement>,
    lockArea: boolean = false
): boolean => {
    const currentInput = inputRef.current;
    if (!currentInput) return false;

    const allInputFields = lockArea
        ? getAllXadomInputsInTable(currentInput)
        : getAllXadomInputs();

    const totalFields = allInputFields.length;
    if (totalFields === 0) return false;

    let currentIndex = allInputFields.indexOf(currentInput);
    if (currentIndex === -1) return false;

    if (currentIndex === 0) return false;

    let prevIndex = currentIndex - 1;
    let fieldsChecked = 0;

    while (fieldsChecked < currentIndex) {
        const prevInputField = allInputFields[prevIndex] as HTMLElement;

        if (!prevInputField.classList.contains('readonly')) {
            prevInputField.focus();
            return true;
        }

        prevIndex--;
        fieldsChecked++;

        if (prevIndex < 0) break;
    }

    return false; // 포커스 가능한 요소를 찾지 못한 경우
};

/**
 * Focus the input field above the current one.
 * @param inputRef - Reference to the current input element.
 * @param lockArea - If true, restrict focus within the current table.
 */
export const focusUpInput = (
    inputRef: React.RefObject<HTMLElement>,
    lockArea: boolean = false
): boolean => {
    const currentInput = inputRef.current;
    if (!currentInput) return false;

    const allInputs = lockArea
        ? getAllXadomInputsInTable(currentInput)
        : getAllXadomInputs();

    const grid = buildPositionGrid(allInputs as HTMLElement[]);
    const { row, col } = findElementPosition(grid, currentInput);

    if (row <= 0) return false;

    const prevRow = grid.rows[row - 1];
    const targetCol = Math.min(col, prevRow.length - 1);
    const targetInput = prevRow[targetCol];

    if (targetInput && !targetInput.classList.contains('readonly')) {
        targetInput.focus();
        return true;
    }
    return false;
};

/**
 * Focus the input field below the current one.
 * @param inputRef - Reference to the current input element.
 * @param lockArea - If true, restrict focus within the current table.
 */
export const focusDownInput = (
    inputRef: React.RefObject<HTMLElement>,
    lockArea: boolean = false
): boolean => {
    const currentInput = inputRef.current;
    if (!currentInput) return false;

    const allInputs = lockArea
        ? getAllXadomInputsInTable(currentInput)
        : getAllXadomInputs();

    const grid = buildPositionGrid(allInputs as HTMLElement[]);
    const { row, col } = findElementPosition(grid, currentInput);

    if (row === -1 || row >= grid.rows.length - 1) return false;

    const nextRow = grid.rows[row + 1];
    const targetCol = Math.min(col, nextRow.length - 1);
    const targetInput = nextRow[targetCol];

    if (targetInput && !targetInput.classList.contains('readonly')) {
        targetInput.focus();
        return true;
    }
    return false;
};

export const dateToFormattedString = (date: string | Date, mask: string): string | null => {
    if (!date) return null;
    return moment(date).format(mask);
};

export const formattedStringToDate = (value: string, mask: string): Date | null => {
    if (!value) return null;

    const isCorrectFormat = moment(value, mask, true).isValid();
    if (!isCorrectFormat) return null

    const parsedDate = new Date(value);
    if (isNaN(parsedDate.getTime())) {
        return null;
    }
    return parsedDate;
}

export const vaildateDateString = (value: string, mask: string): boolean => {
    // 고정 년도가 있을 경우 전체 마스크로 변환
    const momentDate = moment(value, mask, true);
    if (!momentDate.isValid()) return false;

    return true;
}


// ActionPayload 타입 정의
export type ActionPayload<T = any> = T;

// FormAction 인터페이스를 제네릭으로 정의하여 타입 안전성 강화
export interface FormAction<T = any> extends PayloadAction<T> {
    type: string;
}

// 필드 이름에 따라 액션 이름을 생성하는 함수
export const createActionName = (fieldName: string): string => {
    return `set${fieldName.charAt(0).toUpperCase()}${fieldName.slice(1)}`;
};

// 액션 생성기를 찾는 함수
export const findActionCreator = <
    T extends SliceCaseReducers<any>,
    Actions extends CaseReducerActions<T, any>,
    FieldName extends string
>(
    fieldName: FieldName,
    actions: Actions
): ((payload: any) => PayloadAction<any>) | undefined => {
    const actionKey = createActionName(fieldName) as keyof Actions;
    const actionCreator = actions[actionKey];

    if (actionCreator && typeof actionCreator === 'function') {
        return actionCreator as (payload: any) => PayloadAction<any>;
    }
    return undefined;
};

// HandleChangeProps 인터페이스 정의
interface HandleChangeProps<Actions> {
    dispatch: Dispatch<PayloadAction<any>>;
    actions: Actions;
}

// useHandleChange 훅 정의
export const useHandleChange = <Actions extends CaseReducerActions<SliceCaseReducers<any>, any>>(
    { dispatch, actions }: HandleChangeProps<Actions>
) => {
    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        const { name, value, type } = e.target;

        // 체크박스의 경우 checked 값을 사용
        const payload = type === 'checkbox' && e.target instanceof HTMLInputElement ? e.target.checked : value;

        const actionCreator = findActionCreator(name, actions);

        if (actionCreator) {
            const action = actionCreator(payload);
            if (action && 'payload' in action && 'type' in action) {
                dispatch(action);
            }
        }
    };

    return handleChange;
};

export const numberToPaddedString = (num: number, length: number): string => {
    return num.toString().padStart(length, '0');
};

// Toast 이벤트 디스패처
const toastEvents = new EventTarget();

/**
 * 사용자에게 알림(Toast)을 보여주는 유틸 함수입니다.
 *
 * @param message - 사용자에게 보여줄 메시지 문자열
 * @param type - 알림의 타입 ('success' | 'error' | 'warning' | 'info' | 'default')
 * @param durationOrCallback - (선택) 표시 시간 또는 콜백 함수 (기본값: 타입별 자동 설정)
 *
 * @example
 * ```ts
 * showToast("저장이 완료되었습니다", "success"); // 2초 후 자동 닫기
 * showToast("에러가 발생했습니다", "error"); // 수동 닫기 (클릭해서 닫기)
 * showToast("경고 메시지", "warning", 3000); // 3초 후 자동 닫기
 * showToast("처리 완료", "success", () => console.log("닫힘")); // 콜백과 함께
 * ```
 */
export const showToast = (
    message: string,
    type: 'success' | 'error' | 'warning' | 'info' | 'default' = 'default',
    durationOrCallback?: number | (() => void)
) => {
    // 세 번째 파라미터가 함수인지 숫자인지 확인
    let duration: number | undefined;
    let callback: (() => void) | undefined;
    
    if (typeof durationOrCallback === 'number') {
        duration = durationOrCallback;
    } else if (typeof durationOrCallback === 'function') {
        // 콜백 함수는 무시하고 기본 duration 사용
        callback = durationOrCallback;
    } else {
        duration = durationOrCallback; // undefined
    }

    const event = new CustomEvent('show-toast', {
        detail: { message, type, duration, onClose: callback },
    });
    toastEvents.dispatchEvent(event);
};

export { toastEvents };

/**
 * 일반 객체를 XadomTable Cell 형태로 변환하는 함수
 * 
 * 각 속성값을 { value: ... } 형태로 래핑하여 XadomTable에서 사용할 수 있는 Cell 형태로 변환합니다.
 * 
 * @example
 * // 입력
 * { name: 'John Doe', age: 30 }
 * 
 * // 출력
 * { name: { value: 'John Doe' }, age: { value: 30 } }
 * 
 * @param {any} obj - 변환할 일반 객체
 * @returns {any} XadomTable Cell 형태의 객체
 */
export const convertToTableCellFormat = (obj: any) => {
    const result: any = {};
    Object.keys(obj).forEach(key => {
        result[key] = { value: obj[key] };
    });
    return result;
};


/**
 * payload 내 필드들의 유효성과 필수값을 검사하는 함수입니다.
 * 
 * @param payload - 검사 대상 객체
 * @param rules - 유효성 검사 규칙 배열
 *   각 규칙은 다음 속성을 가집니다:
 *    - field: payload 내 검사할 필드 경로 (예: 'email', 'user.age')
 *    - message: 검증 실패 시 반환할 에러 메시지
 *    - validate?: (선택) 커스텀 유효성 검사 함수 (true면 유효)
 *    - allowEmptyObject?: (선택) 빈 객체를 허용할지 여부 (기본 false)
 * 
 * 
 * 동작 방식:
 * 1. 각 rule에 대해 payload에서 해당 필드 값을 찾아 검사합니다.
 * 2. rule에 validate 함수가 있으면 이를 사용해 검사합니다.
 * 3. 없으면 기본적으로 값이 없거나(null/undefined), 빈 객체(allowEmptyObject가 false일 때), 숫자인 경우 0 일때, 
 *    또는 빈 문자열인 경우 유효하지 않은 것으로 간주합니다.
 * 4. 검사에 실패하면 즉시 해당 rule의 message를 반환합니다.
 * 5. 모든 검사가 통과하면 null을 반환합니다.
 * 
 * 
 *@example
 * 기본 유효성 검사(email) + 커스텀 validate 포함(age)
 * const payload = {
 *   email: "", =>  빈 값
 *   age: 17,
 * };
 * 
 * const rules = [
 *   {
 *     field: 'email',
 *     message: '이메일을 입력해주세요',
 *   },
 *   {
 *     field: 'age',
 *     message: '성인은 18세 이상입니다',
 *     validate: (val) => typeof val === 'number' && val >= 18
 *   }
 * ];
 * 
 * const errorMessage = validatePayloadFields(payload, rules);
 * 
 * if (errorMessage) {
 *   alert(errorMessage); // 👉 '이메일을 입력해주세요' 
 * }
 * 
 */
export function validatePayloadFields(
    payload: any,
    rules: {
        field: string;
        message: string;
        validate?: (value: any) => boolean;
        allowEmptyObject?: boolean;
    }[]
): string | null {
    if (!payload || typeof payload !== 'object' || Object.keys(payload).length === 0) {
        return '저장할 내용이 없습니다.';
    }

    for (const rule of rules) {
        const value = rule.field.split('.').reduce((obj, key) => obj?.[key], payload);

        let isValid: boolean;
        // rule에 유효성 검증 함수가 있을때 
        if (rule.validate) {
            isValid = rule.validate(value);
        }
        // value값이 없을때
        else if (value === undefined || value === null) {
            isValid = false;
        }
        // value값이 객체이고 안에 필수로 들어가야할때 사용, 안에 값이 하나라도 있으면 유효
        else if (typeof value === 'object' && !rule.allowEmptyObject) {
            isValid = Object.keys(value).length > 0;
        }
        // 빈 문자열 검사
        else if (typeof value === 'string') {
            isValid = value.trim() !== '';
        }
        // 숫자가 0이면 유효하지 않은 값으로 처리
        else if (typeof value === 'number') {
            isValid = value !== 0;
        }
        else {
            isValid = true;
        }
        if (!isValid) {
            return rule.message;
        }
    }
    return null;
}

/**
 * 회계 기간 객체에서 년도를 추출하는 유틸리티 함수들
 */

/**
 * 회계 기간의 시작일에서 년도를 추출합니다.
 * @param accountingPeriod - 회계 기간 객체 (startDate, endDate 포함)
 * @returns 시작일의 년도 (문자열)
 */
export const getAccountingStartYear = (accountingPeriod?: { startDate?: string }): string => {
    if (!accountingPeriod?.startDate) return '';
    return accountingPeriod.startDate.split('-')[0];
};

/**
 * 회계 기간의 종료일에서 년도를 추출합니다.
 * @param accountingPeriod - 회계 기간 객체 (startDate, endDate 포함)
 * @returns 종료일의 년도 (문자열)
 */
export const getAccountingEndYear = (accountingPeriod?: { endDate?: string }): string => {
    if (!accountingPeriod?.endDate) return '';
    return accountingPeriod.endDate.split('-')[0];
};

/**
 * 회계 기간에서 시작일과 종료일의 년도를 모두 추출합니다.
 * @param accountingPeriod - 회계 기간 객체 (startDate, endDate 포함)
 * @returns { startYear: string, endYear: string }
 */
export const getAccountingYears = (accountingPeriod?: { startDate?: string; endDate?: string }) => {
    return {
        startYear: getAccountingStartYear(accountingPeriod),
        endYear: getAccountingEndYear(accountingPeriod)
    };
};

/**
 * CompanyInfo 객체에서 회계 기간 년도를 추출합니다.
 * @param companyInfo - CompanyInfo 객체
 * @returns { startYear: string, endYear: string }
 */
export const getCompanyAccountingYears = (companyInfo?: { accountingPeriod?: { startDate?: string; endDate?: string } }) => {
    return getAccountingYears(companyInfo?.accountingPeriod);
};

/**
 * 회계 기간을 기반으로 월별 선택 옵션을 생성합니다.
 * @param accountingPeriod - 회계 기간 객체 (startDate, endDate 포함)
 * @returns 월별 옵션 배열 [{ value: "YYYY-MM", label: "YYYY.MM" }, ...]
 * 
 * @example
 * const accountingPeriod = { startDate: "2025-01-01", endDate: "2025-12-31" };
 * const options = getAccountingPeriodMonthOptions(accountingPeriod);
 * // 결과: [{ value: "2025-01", label: "2025.01" }, ..., { value: "2025-12", label: "2025.12" }]
 */
export const getAccountingPeriodMonthOptions = (accountingPeriod?: { startDate?: string; endDate?: string }) => {
    if (!accountingPeriod?.startDate || !accountingPeriod?.endDate) {
        return [];
    }

    const startDate = new Date(accountingPeriod.startDate);
    const endDate = new Date(accountingPeriod.endDate);
    
    const monthOptions = [];
    const currentDate = new Date(startDate);
    
    while (currentDate <= endDate) {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth() + 1; // getMonth()는 0부터 시작하므로 +1
        const monthStr = month.toString().padStart(2, '0');
        
        monthOptions.push({
            value: `${year}-${monthStr}`,
            label: `${year}.${monthStr}`,
        });
        
        // 다음 달로 이동
        currentDate.setMonth(currentDate.getMonth() + 1);
    }
    
    return monthOptions;
};